import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, RotateCcw, ImagePlus } from "lucide-react";
import { toast } from "sonner";

/**
 * Camera capture + upload picker. Emits a base64 data URL via `onCapture`.
 * On mobile the OS picks the rear camera automatically (facingMode: "environment").
 */
export default function CameraCapture({ onCapture }) {
  const [open, setOpen] = useState(false);
  const [stream, setStream] = useState(null);
  const [preview, setPreview] = useState(null);
  const [starting, setStarting] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const stopStream = () => {
    if (stream) {
      stream.getTracks().forEach((t) => t.stop());
      setStream(null);
    }
  };

  const start = async () => {
    setStarting(true);
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      setStream(s);
      setOpen(true);
      setPreview(null);
    } catch (e) {
      toast.error("Camera unavailable. Please allow camera access or upload a photo instead.");
    } finally {
      setStarting(false);
    }
  };

  useEffect(() => {
    if (open && videoRef.current && stream) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
    return () => {
      if (!open) return;
    };
  }, [open, stream]);

  useEffect(() => () => stopStream(), []); // eslint-disable-line

  const capture = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth;
    const h = v.videoHeight;
    if (!w || !h) return;
    // Scale down to <= 1600px on the longest side so payload stays small
    const max = 1600;
    const scale = Math.min(1, max / Math.max(w, h));
    c.width = Math.round(w * scale);
    c.height = Math.round(h * scale);
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL("image/jpeg", 0.82);
    setPreview(dataUrl);
  };

  const confirm = () => {
    if (preview) onCapture(preview);
    stopStream();
    setOpen(false);
    setPreview(null);
  };

  const retake = () => setPreview(null);

  const close = () => {
    stopStream();
    setOpen(false);
    setPreview(null);
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onCapture(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={start}
          disabled={starting}
          data-testid="camera-open-btn"
          className="inline-flex items-center gap-2 border border-[#143D63]/25 bg-white text-[#143D63] px-4 py-2.5 text-sm font-semibold hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors disabled:opacity-60"
        >
          <Camera size={15} /> {starting ? "Opening…" : "Use Camera"}
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          data-testid="camera-upload-btn"
          className="inline-flex items-center gap-2 border border-[#143D63]/25 bg-white text-[#143D63] px-4 py-2.5 text-sm font-semibold hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors"
        >
          <ImagePlus size={15} /> Upload photo
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onFile}
          className="hidden"
          data-testid="camera-file-input"
        />
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#0A0A0A]/85 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="camera-modal"
          >
            <div className="relative w-full max-w-2xl bg-[#0A0A0A] border border-[#F8C21C]/30">
              <button
                onClick={close}
                data-testid="camera-close-btn"
                className="absolute top-3 right-3 z-10 text-[#F9F8F6] bg-[#0A0A0A]/60 hover:bg-[#8B0000] p-2 transition-colors"
                aria-label="Close camera"
              >
                <X size={18} />
              </button>
              <div className="aspect-video relative overflow-hidden bg-black">
                {!preview ? (
                  <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />
                ) : (
                  <img src={preview} alt="preview" className="w-full h-full object-cover" />
                )}
                <canvas ref={canvasRef} className="hidden" />
              </div>
              <div className="p-4 flex items-center justify-between gap-2 bg-[#0A0A0A] border-t border-[#F8C21C]/20">
                <p className="text-xs text-[#F9F8F6]/70">Point at the wall, floor plan or room you want redesigned.</p>
                {!preview ? (
                  <button
                    onClick={capture}
                    data-testid="camera-capture-btn"
                    className="bg-[#F8C21C] text-[#143D63] px-5 py-2.5 text-sm font-bold hover:bg-[#F9F8F6] transition-colors"
                  >
                    Capture
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button
                      onClick={retake}
                      data-testid="camera-retake-btn"
                      className="inline-flex items-center gap-2 border border-[#F9F8F6]/25 text-[#F9F8F6] px-4 py-2.5 text-sm hover:bg-[#F9F8F6]/10"
                    >
                      <RotateCcw size={14} /> Retake
                    </button>
                    <button
                      onClick={confirm}
                      data-testid="camera-use-btn"
                      className="bg-[#F8C21C] text-[#143D63] px-5 py-2.5 text-sm font-bold hover:bg-[#F9F8F6] transition-colors"
                    >
                      Use photo
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
